import classcat from "classcat";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import tinycolor from "tinycolor2";

import { ServiceWorkerContext } from "@touchtech/web-service-worker-provider";

import { Logo } from "@is/components/Logo";
import { Media } from "@is/components/Media";
import {
  LocalStorageKeys,
  MediaOrientation,
  MediaVariants,
  ROUTES,
  SOCKET_MESSAGE_TYPE,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import {
  useConfiguration,
  useProcessPendingEvents,
  useScreenOrientation,
  useSession,
  useStoreCountries,
  useWebSocketConnect,
  useWebSocketWorkerMessage,
} from "@is/hooks";
import useScanner from "@is/hooks/scanner/useScanner";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { AttributeSetting, HTWIContentWidget, SearchTerm } from "@is/types";
import { getEnvValue } from "@is/utils/env";

import css from "../EntryView.module.css";
import { useManageEntryWidgets } from "./useManageEntryWidgets";

const useManageEntryView = () => {
  const { t } = useTranslation(TranslationContext.ProductListing);

  const { attributes, appearance, backgroundMedia, localizedMedia, isLoading } =
    useConfiguration({
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      shouldRetryOnError: true,
    });

  const { widget, isWidgetPending } = useManageEntryWidgets();

  const navigate = useNavigate();

  const { countries } = useStoreCountries();

  const { isPortraitView, isLandscapeView } = useScreenOrientation();

  const countryCode = LocalStorageService.getItem(LocalStorageKeys.CountryCode);

  const { showStyleNotExistModal, handleCloseModal } = useScanner();

  const { hasUpdate, skipWaiting } = useContext(ServiceWorkerContext);

  const ref = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);

  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [showWidgetOverlay, setShowWidgetOverlay] = useState<boolean>(false);
  const [isWidgetTransitioning, setIsWidgetTransitioning] =
    useState<boolean>(false);

  const { connect } = useWebSocketConnect();

  const {
    updateViewState,
    animateLeavingEntryView,
    showPrimaryAttributeDrawer,
    showSettingsDrawer,
    signUpStatusModal,
    externalPageDrawer,
  } = useContext(ViewStateContext).content;

  const primaryAttributes = useMemo(
    () => attributes?.filter((attr) => attr.primary),
    [attributes]
  );

  const { sendMessage } = useWebSocketWorkerMessage((event: MessageEvent) => {
    switch (event.data.type) {
      case SOCKET_MESSAGE_TYPE.INITIALIZE_DEVICE: {
        const deviceId = LocalStorageService.getItem(LocalStorageKeys.DeviceId);
        const tenantId = LocalStorageService.getItem(LocalStorageKeys.TenantId);
        const storeId = LocalStorageService.getItem(LocalStorageKeys.StoreId);
        const version = LocalStorageService.getItem(LocalStorageKeys.Version);
        const lastUpdateAt = LocalStorageService.getItem(
          LocalStorageKeys.LastUpdateAt
        );

        if (deviceId && tenantId && storeId) {
          const params = {
            deviceId,
            tenantId,
            storeId,
          };

          if (version) {
            params["version"] = {
              version,
              appliedAt: lastUpdateAt.replace(/"/g, ""),
            };
          }

          sendMessage({
            type: SOCKET_MESSAGE_TYPE.INITIALIZE_DEVICE,
            params,
          });
        }

        break;
      }
    }
  });

  const { createSession, isSessionExist, isSessionFinished } = useSession();

  const navigateToContentView = useCallback(
    (searchTerms?: SearchTerm[]) => {
      updateViewState({
        animateLeavingEntryView: true,
        showPrimaryAttributeDrawer: false,
        ...(searchTerms && {
          searchTerms,
        }),
      });
      setTimeout(() => {
        navigate(`${ROUTES.ContentView}`);
      }, 1000);
    },
    [navigate, updateViewState]
  );

  const handleOpenDrawer = useCallback(() => {
    let primaryAttributeStep = null;
    primaryAttributes.every((attribute, index) => {
      if (attribute.term || !attribute.visible) return true;
      primaryAttributeStep = index;
    });

    const searchTerms = primaryAttributes.reduce(
      (acc: SearchTerm[], attribute: AttributeSetting) => [
        ...acc,
        ...(attribute.term ? [{ ...attribute.term, isPrimary: true }] : []),
      ],
      []
    );

    if (showPrimaryAttributeDrawer || primaryAttributeStep !== null) {
      updateViewState({
        showPrimaryAttributeDrawer: !showPrimaryAttributeDrawer,
        showPrimaryAttributeButtons: false,
      });

      trackEvent({
        eventType: TrackingEventTypes.PrimaryAttributesDrawerOpen,
        data: {},
      });

      return;
    }

    updateViewState({ showPrimaryAttributeButtons: false });
    navigateToContentView(searchTerms);
  }, [
    primaryAttributes,
    navigateToContentView,
    showPrimaryAttributeDrawer,
    updateViewState,
  ]);

  const handleSettingDrawerClose = useCallback(() => {
    updateViewState({ showSettingsDrawer: false });
  }, [updateViewState]);

  const handleSignUpStatusModalClose = useCallback(() => {
    updateViewState({
      signUpStatusModal: {
        isOpen: false,
        status: null,
        errorMessage: null,
      },
    });
  }, [updateViewState]);

  const handleExternalPageDrawerClose = useCallback(() => {
    updateViewState({
      externalPageDrawer: {
        isOpen: false,
        src: null,
      },
    });
  }, [updateViewState]);

  const restartVideo = useCallback(() => {
    const videoElement = mediaRef.current?.querySelector("video");
    if (videoElement) {
      videoElement.currentTime = 0;
      const playPromise = videoElement.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn("Video play interrupted:", error.message);
        });
      }
    }
  }, []);

  const handleClick = useCallback(
    (event: Event) => {
      event.stopPropagation();

      skipWaiting();
    },
    [skipWaiting]
  );
  const handleTouchStart = useCallback(
    (event: Event) => {
      event.stopPropagation();
      event.preventDefault();

      skipWaiting();
    },
    [skipWaiting]
  );

  const videoDurationMs = useMemo(() => {
    return videoDuration !== null ? Math.round(videoDuration * 1000) : null;
  }, [videoDuration]);

  const widgetSlidesCount = useMemo(() => {
    if (!widget) return 0;
    return (widget as HTWIContentWidget).items?.length || 0;
  }, [widget]);

  const widgetDisplayDuration = useMemo(() => {
    return widgetSlidesCount * 5000 * 2;
  }, [widgetSlidesCount]);

  const mediaWaitDuration = useMemo(() => {
    return videoDurationMs ?? 20000;
  }, [videoDurationMs]);

  const shouldShowWidget = useMemo(() => {
    return widget && !isWidgetPending && mediaWaitDuration;
  }, [widget, isWidgetPending, mediaWaitDuration]);

  const mediaElement = useMemo(() => {
    if (isLoading) {
      return <div></div>;
    }

    const languageCode = LocalStorageService.getItem(
      LocalStorageKeys.LanguageCode
    );

    const orientation =
      window.innerWidth > window.innerHeight
        ? MediaOrientation.Horizontal
        : MediaOrientation.Vertical;

    const localizedMediaOption = localizedMedia.find(
      (media) => media.languages.includes(languageCode) && media[orientation]
    );

    let media = null;
    if (localizedMediaOption?.[orientation]) {
      media = localizedMediaOption[orientation];
    } else if (backgroundMedia?.[orientation]) {
      media = backgroundMedia[orientation];
    }

    if (media) {
      return (
        <div ref={mediaRef} className={css.mediaContainer}>
          <Media
            media={media}
            mediavariant={MediaVariants.HUGE}
            showloadingspinner
          />
        </div>
      );
    }

    const style = {};

    if (appearance?.colors?.primary) {
      style["backgroundColor"] = tinycolor(appearance?.colors?.primary)
        .brighten(75)
        .toString();
    }

    return (
      <div
        style={style}
        className={classcat([
          css.noMediaPlaceholder,
          {
            [css.portrait]: isPortraitView,
            [css.landscape]: isLandscapeView,
          },
        ])}
      >
        <Logo className={css.logo} showPoweredBy={false} />
      </div>
    );
  }, [
    backgroundMedia,
    localizedMedia,
    appearance,
    isLoading,
    isPortraitView,
    isLandscapeView,
  ]);

  useEffect(() => {
    window.addEventListener("focus", connect);
    return () => {
      window.removeEventListener("focus", connect);
    };
  }, [connect]);

  useEffect(() => {
    connect();
  }, [connect]);

  useEffect(() => {
    // A session that already completed checkout has a basket the backend has
    // locked, so reusing it makes every basket update fail silently. Reject it
    // here as well as when it is missing (ticket aecd58ce).
    if (!isSessionExist || isSessionFinished) {
      createSession();
    }
  }, [createSession, isSessionExist, isSessionFinished]);

  useEffect(() => {
    if (!countryCode && countries?.length) {
      LocalStorageService.setItem(LocalStorageKeys.CountryCode, countries[0]);
    }
  }, [countries, countryCode]);

  useEffect(() => {
    if (backgroundMedia) {
      LocalStorageService.setItem(
        LocalStorageKeys.BackgroundMedia,
        backgroundMedia
      );
    }
  }, [backgroundMedia]);

  useEffect(
    () => {
      const element = ref.current;

      if (
        element &&
        hasUpdate &&
        getEnvValue("VITE_SERVICE_WORKER") === "true"
      ) {
        element.addEventListener("click", handleClick, { capture: true });
        element.addEventListener("touchstart", handleTouchStart, {
          capture: true,
        });

        return () => {
          element.removeEventListener("click", handleClick, { capture: true });
          element.removeEventListener("touchstart", handleTouchStart, {
            capture: true,
          });
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpdate]
  );

  useEffect(() => {
    if (!localizedMedia.length) {
      setVideoDuration(null);
      return;
    }

    const languageCode = LocalStorageService.getItem(
      LocalStorageKeys.LanguageCode
    );

    const orientation =
      window.innerWidth > window.innerHeight
        ? MediaOrientation.Horizontal
        : MediaOrientation.Vertical;

    const localizedMediaOption = localizedMedia.find(
      (media) => media.languages.includes(languageCode) && media[orientation]
    );

    let media = null;
    if (localizedMediaOption?.[orientation]) {
      media = localizedMediaOption[orientation];
    } else if (backgroundMedia?.[orientation]) {
      media = backgroundMedia[orientation];
    }

    if (media?.type?.startsWith("video/")) {
      const setupVideoElement = () => {
        const videoElement = mediaRef.current?.querySelector("video");

        if (videoElement) {
          const handleLoadedMetadata = () => {
            setVideoDuration(videoElement.duration);
          };

          const handleError = () => {
            setVideoDuration(null);
          };

          if (videoElement.readyState >= 1) {
            setVideoDuration(videoElement.duration);
          } else {
            videoElement.addEventListener(
              "loadedmetadata",
              handleLoadedMetadata
            );
            videoElement.addEventListener("error", handleError);
          }

          return () => {
            videoElement.removeEventListener(
              "loadedmetadata",
              handleLoadedMetadata
            );
            videoElement.removeEventListener("error", handleError);
          };
        }
        return null;
      };

      let videoCleanup: (() => void) | null = null;
      let pollInterval: NodeJS.Timeout | null = null;

      const cleanup = setupVideoElement();
      if (cleanup) {
        videoCleanup = cleanup;
      } else {
        pollInterval = setInterval(() => {
          const cleanup = setupVideoElement();
          if (cleanup) {
            videoCleanup = cleanup;
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }, 100);
      }

      return () => {
        if (pollInterval) {
          clearInterval(pollInterval);
        }
        if (videoCleanup) {
          videoCleanup();
        }
      };
    } else {
      setVideoDuration(null);
    }
  }, [backgroundMedia, localizedMedia]);

  useEffect(() => {
    if (!shouldShowWidget) {
      setShowWidgetOverlay(false);
      return;
    }

    let waitTimer: NodeJS.Timeout;
    let showTimer: NodeJS.Timeout;
    let hideTimer: NodeJS.Timeout;
    let exitTimer: NodeJS.Timeout;
    let isMounted = true;

    const startCycle = () => {
      if (!isMounted) return;

      waitTimer = setTimeout(() => {
        if (!isMounted) return;
        setIsWidgetTransitioning(true);
        showTimer = setTimeout(() => {
          if (!isMounted) return;
          setShowWidgetOverlay(true);
          setIsWidgetTransitioning(false);

          hideTimer = setTimeout(() => {
            if (!isMounted) return;
            setIsWidgetTransitioning(true);
            exitTimer = setTimeout(() => {
              if (!isMounted) return;
              setShowWidgetOverlay(false);
              setIsWidgetTransitioning(false);
              restartVideo();
              startCycle();
            }, 500);
          }, widgetDisplayDuration);
        }, 500);
      }, mediaWaitDuration);
    };

    startCycle();

    return () => {
      isMounted = false;
      clearTimeout(waitTimer);
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(exitTimer);
    };
  }, [
    shouldShowWidget,
    widgetDisplayDuration,
    mediaWaitDuration,
    restartVideo,
  ]);

  useProcessPendingEvents();

  return {
    t,
    handleOpenDrawer,
    handleCloseModal,
    navigateToContentView,
    showStyleNotExistModal,
    mediaElement,
    videoDurationMs,
    animateLeavingEntryView,
    showSettingsDrawer,
    handleSettingDrawerClose,
    signUpStatusModal,
    handleSignUpStatusModalClose,
    externalPageDrawer,
    handleExternalPageDrawerClose,
    widget,
    showWidgetOverlay,
    isWidgetTransitioning,
    ref,
    restartVideo,
  };
};

export default useManageEntryView;
